import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import './DataDefinitionRow.less';
import { DecisionsType } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { InlineMapping } from '@decisions/server/DecisionsFramework/Design/Flow';
import { IconButton } from 'components/buttons/IconButton';
import { TextField } from 'components/inputs/TextField';
import { Checkbox } from 'components/inputs/Checkbox';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { TypeComboPicker } from 'components/inputs/TypeComboPicker';
import { useDebounceStringProperty } from 'hooks/useDebounceStringProperty';
import { isNotEmpty } from 'utils/ArrayUtils';
import { getValidationSummary } from 'utils/ValidationUtils';
import { InlineEditor } from 'domains/designers/TruthTable/components';
import { IntersectionEditorInfo } from 'domains/designers/TruthTable/components/InlineEditor';
import { DecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/DecisionsTypePickerSettings';
import { PropertyGridLayer } from 'components/property-editors/PropertyGrid';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { SequentialEditSession } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/Mapping/SequentialEditSession';
import { showSequentialPicker } from 'domains/designers/common/Utils';
import { useTrackFocus } from 'components/inputs/hooks/useTrackFocus';

export interface IDataDefinition {
  Id: string;
  DataName: string;
  DataType: DecisionsType;
  IsArray: boolean;
  InlineMapping?: InlineMapping;
  ValidationIssues: ValidationIssue[];
}

export interface IDataDefinitionRowCommonProps {
  showInputColumn?: boolean;
  mappingContext?: MappingContext;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  onNameChanged: (index: number, newName: string) => Promise<string>;
  onIsListChanged: (index: number, isList: boolean) => void;
  onTypeChanged: (index: number, type: DecisionsType | undefined) => void;
  onDelete: (index: number) => void;
  onConstantValueChanged?: (index: number, value: string) => Promise<void>;
  onRowSelected?: (index: number, definition: IDataDefinition) => void;
  onGetEditMappingContextId?: (index: number) => Promise<string>;
  startEditSelectMapping?: (index: number) => Promise<SequentialEditSession>;
}

export interface IDataDefinitionRowProps extends React.ComponentProps<'tr'>, IDataDefinitionRowCommonProps {
  index: number;
  isFirst: boolean;
  isLast: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  definition: IDataDefinition;
  isSelected: boolean;
  /**
   * Determines whether it is possible to re-order row
   */
  reorderableRow: boolean;
  settings: DecisionsTypePickerSettings | null;
  onNameBlur: (rowIndex: number) => void;
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DataDefinitionRow({
  index,
  isFirst,
  isLast,
  canMoveUp,
  canMoveDown,
  definition,
  showInputColumn,
  className,
  isSelected,
  reorderableRow,
  settings,
  onDelete,
  onIsListChanged,
  onNameChanged,
  onTypeChanged,
  onRowSelected,
  onMoveUp,
  onMoveDown,
  onNameBlur,
  onConstantValueChanged,
  onGetEditMappingContextId,
  mappingContext,
  startEditSelectMapping,
  ...otherProps
}: IDataDefinitionRowProps) {
  const inputNameRef = useRef<HTMLInputElement | null>(null);
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  // we should not allow delete/move last row with empty name
  const isLastEmptyRow = isLast && !definition.DataName;

  const [isLayerVisible, setIsLayerVisible] = useState<boolean>(false);
  const valueLayerRef = useRef<HTMLElement | null>(null);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.checked;
      onIsListChanged(index, value);
    },
    [index, onIsListChanged]
  );

  const handleNameBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(() => {
    onNameBlur(index);
  }, [index, onNameBlur]);

  const {
    focusTrackingProps: { onBlur, onFocus },
    isFocused,
  } = useTrackFocus({ onBlur: handleNameBlur, onFocus: () => handleSelected() });

  const { handleChange, fieldValue, emitOnBlur } = useDebounceStringProperty({
    onChange: useCallback(
      async (value: string) => {
        await onNameChanged(index, value);
      },
      [index, onNameChanged]
    ),
    path: [],
    value: definition.DataName,
    skipValueUpdate: isFocused,
  });

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      onBlur(e);
      emitOnBlur();
    },
    [emitOnBlur, onBlur]
  );

  const handleTypeSelected = useCallback(
    async (type: DecisionsType | null) => {
      if (type) {
        onTypeChanged(index, type);
      }
    },
    [index, onTypeChanged]
  );

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
    valueLayerRef.current = e.target as HTMLElement;
    setIsLayerVisible(true);
  };

  const handleConstantValueChanged = useCallback(
    (newValue: string) => {
      onConstantValueChanged?.(index, newValue);
    },
    [index, onConstantValueChanged]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      // prevent selecting row when click on delete button as it breaks selecting next row when deleting
      e.stopPropagation();
      onDelete(index);
    },
    [index, onDelete]
  );

  const handleSelected = useCallback(() => {
    onRowSelected?.(index, definition);
  }, [definition, index, onRowSelected]);

  const handleMoveUp = useCallback(() => {
    onMoveUp?.(index);
  }, [index, onMoveUp]);

  const handleMoveDown = useCallback(() => {
    onMoveDown?.(index);
  }, [index, onMoveDown]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // to prevent sending both events to the server "onNameChanged" and "onDelete" we suppress handling shortcuts input
    // it happens when text in input is selected and press "Shift + Delete"
    if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Delete')) {
      e.preventDefault();
    }
  }, []);

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      if (!e.shiftKey || isLastEmptyRow) {
        return;
      }

      if (e.key === 'ArrowUp' && canMoveUp && onMoveUp != null) {
        onMoveUp(index);
      } else if (e.key === 'ArrowDown' && canMoveDown && onMoveDown != null) {
        onMoveDown(index);
      } else if (e.key === 'Delete') {
        onDelete(index);
      }
    },
    [canMoveDown, canMoveUp, index, isLastEmptyRow, onDelete, onMoveDown, onMoveUp]
  );

  useEffect(() => {
    if (inputNameRef.current == null || rowRef.current == null || document.activeElement == null) return;

    // if row isSelected and no activeElement inside row then focus inputName
    // For Move Up/Move Down via keyboard.
    if (isSelected && !rowRef.current.contains(document.activeElement)) {
      inputNameRef.current.focus();
    }
  }, [isSelected]);

  // We do not show validation for last row as usually last row is auto added and has validation as name is empty.
  // But we don't want to confuse user by showing validation.
  const hasIssues = isLast ? false : isNotEmpty(definition.ValidationIssues);
  const validationSummary = getValidationSummary((isLast ? [] : definition.ValidationIssues) ?? []);

  const editorInfo: IntersectionEditorInfo = useMemo(() => {
    return {
      mappingType: definition.InlineMapping?.MappingType ?? '',
      dataType: definition.InlineMapping?.EditorType ?? '',
      displayValue: definition.InlineMapping?.Value ?? '',
      canNavigate: false,
      initialViewType: 'editor',
      commands: {
        setIntersectionStringValue: (intersection: IntersectionEditorInfo, value: string) => {
          intersection && handleConstantValueChanged(value);
        },
        showFullEditor: (intersection: IntersectionEditorInfo) => {
          if (intersection.mappingType === 'SelectValueInputMapping') {
            startEditSelectMapping?.(index).then(showSequentialPicker);
          }
        },
      },
    };
  }, [definition.InlineMapping, handleConstantValueChanged, startEditSelectMapping, index]);

  return (
    <tr
      ref={rowRef}
      className={classNames('dp-data-definition-row', className, {
        'dp-data-definition-row--invalid': hasIssues,
        'dp-data-definition-row--selected': isSelected,
      })}
      onClick={handleSelected}
      onKeyUp={handleKeyUp}
      {...otherProps}
    >
      <td className="dp-data-definition-row__name-column">
        <div className="dp-data-definition-row__name-cell">
          <TextField
            ref={inputNameRef}
            validation={validationSummary}
            value={fieldValue ?? ''}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={handleBlur}
          />
        </div>
      </td>
      <td className="dp-data-definition-row__type-column">
        <div className="dp-data-definition-row__type-cell">
          <TypeComboPicker
            hideClear={true}
            pickedEntity={definition.DataType != null ? definition.DataType : 'System.String'}
            onSelected={handleTypeSelected}
            onFocus={handleSelected}
            settings={settings}
          />
        </div>
      </td>
      <td className="dp-data-definition-row__is-list-column">
        <div className="dp-data-definition-row__is-list-cell">
          {/* Only name and type should be achivable from keyboard */}
          <Checkbox tabIndex={-1} checked={definition.IsArray} onChange={handleOnChange} />
        </div>
      </td>
      {showInputColumn && (
        <>
          <td className="dp-data-definition-row__input-column">
            {!isLastEmptyRow && (
              <div className="dp-data-definition-row__input-cell">
                {definition.InlineMapping && (
                  <InlineEditor
                    intersection={editorInfo}
                    onTextClick={handleEdit}
                    isInDebug={false}
                    text={definition.InlineMapping?.Value}
                  />
                )}
                {!definition.InlineMapping && <span onClick={handleEdit}>Unknown</span>}
              </div>
            )}
          </td>
          <td className="dp-data-definition-row__edit-column">
            {!isLastEmptyRow && (
              <div className="dp-data-definition-row__edit-cell">
                <IconButton
                  title="Edit Value"
                  className="dp-data-definition-row__edit"
                  iconClassName="dp-data-definition-row__edit-icon"
                  onClick={handleEdit}
                />
              </div>
            )}
          </td>
        </>
      )}
      {isLayerVisible && onGetEditMappingContextId && (
        <PropertyGridLayer
          getEditingContextId={() => onGetEditMappingContextId(index)}
          onClose={() => setIsLayerVisible(false)}
          sourceRef={valueLayerRef}
          mappingContext={mappingContext}
        />
      )}
      <td className="dp-data-definition-row__delete-column">
        {!isLastEmptyRow && (
          <div className="dp-data-definition-row__delete-cell">
            <IconButton
              tabIndex={-1}
              title="Delete (Shift + Delete)"
              className={'dp-data-definition-row__delete'}
              iconClassName={'dp-data-definition-row__delete-icon'}
              onClick={handleDelete}
            />
          </div>
        )}
      </td>
      {reorderableRow && (
        <>
          <td className="dp-data-definition-row__move-up-column">
            {canMoveUp && (
              <div className="dp-data-definition-row__move-up-cell">
                <IconButton
                  tabIndex={-1}
                  title="Move Up (Shift + ↑)"
                  className="dp-data-definition-row__move-up"
                  iconClassName="dp-data-definition-row__move-up-icon"
                  onClick={handleMoveUp}
                />
              </div>
            )}
          </td>
          <td className="dp-data-definition-row__move-down-column">
            {canMoveDown && (
              <div className="dp-data-definition-row__move-down-cell">
                <IconButton
                  tabIndex={-1}
                  title="Move Down (Shift + ↓)"
                  className="dp-data-definition-row__move-down"
                  iconClassName="dp-data-definition-row__move-down-icon"
                  onClick={handleMoveDown}
                />
              </div>
            )}
          </td>
        </>
      )}
    </tr>
  );
}
